/**
 * Miroir CLIENT de la résolution servie par le SSR (`src/server/index.jsx`).
 *
 * `src/index.jsx` monte l'app avec `render()` et non `hydrate()` : le navigateur
 * réécrit intégralement le DOM produit par le serveur. Sans ce miroir, une page
 * 404/410 correctement servie serait immédiatement remplacée par une redirection
 * client vers la page ville — l'utilisateur verrait le contraire de ce que voit
 * Google. Le miroir appelle donc le même endpoint et la MÊME fonction de
 * décision que le serveur (`decideAnnuaireResponse`).
 */
import { API_BASE_URL } from '../utils/apiBaseUrl';
import { lookupRedirect } from './redirectApi';
import { decideAnnuaireResponse, FALLBACK_TO_CITY_PAGE } from './resolvePath';

const CLIENT_TIMEOUT_MS = 5000;

/**
 * `fetch` du navigateur, borné dans le temps.
 *
 * On n'utilise volontairement PAS `src/utils/api` ici : son transport XHR
 * (`src/utils/api.jsx`, `xhr.onload = () => resolve(safeParse())`) résout le
 * corps parsé QUEL QUE SOIT le statut HTTP. Un 500 servi par Laravel avec un
 * corps JSON deviendrait alors un objet sans `found`, donc « aucune Redirection »
 * — une panne déguisée en fait. Le serveur, lui, distingue les deux
 * (`lookupRedirect` jette sur non-2xx). Le miroir doit voir le statut pour
 * raconter la même histoire, sinon une panne api produirait un 404 côté client le
 * jour où le filet passera à `false`.
 */
const timedFetch = (url, options) => {
  if (typeof AbortController === 'undefined') return fetch(url, options);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
  return fetch(url, { ...options, signal: controller.signal }).then(
    (response) => {
      clearTimeout(timer);
      return response;
    },
    (error) => {
      clearTimeout(timer);
      throw error;
    },
  );
};

/**
 * Décide quoi faire d'un chemin d'annuaire dont la fiche n'a pas répondu.
 *
 * @param {string}  requestPath  chemin absolu, ex. `/annuaire-velo/aude/villepinte/xxx`
 * @param {?string} cityUrl      page ville de repli ; `getCityUrl` renvoie `'#'`
 *                               quand elle ne sait pas la construire — on ne
 *                               redirige jamais vers `'#'`.
 * @returns {Promise<Object>} même forme que `decideAnnuaireResponse`
 */
export async function decideDeadAnnuairePath(requestPath, cityUrl) {
  let redirect = null;
  let redirectLookupFailed = false;

  try {
    redirect = await lookupRedirect({
      apiBase: API_BASE_URL,
      path: requestPath,
      fetchFn: timedFetch,
    });
  } catch (error) {
    console.error(
      '[annuaire] lookup client en échec',
      requestPath,
      error.message,
    );
    redirectLookupFailed = true;
  }

  const fallbackLocation =
    FALLBACK_TO_CITY_PAGE && cityUrl && cityUrl !== '#' ? cityUrl : null;

  return decideAnnuaireResponse({
    shopStatus: 'missing',
    redirect,
    redirectLookupFailed,
    fallbackLocation,
    requestPath,
  });
}

export default decideDeadAnnuairePath;
